import { useCallback, type MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { take } from 'lib/array/arrayUtils'
import { trackClientEvent } from 'analytics/analytics'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import SearchTileRowInclusionItem from 'components/SearchKit/SearchTiles/shared/SearchTileRowInclusionItem'

const MAX_VISIBLE_INCLUSIONS = 3
// AmenityGrid supports up to three rows of two amenities; update its classes if this changes.
const MAX_VISIBLE_AMENITIES = 6

const AmenityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-flow: column;
  gap: ${rem(4)} ${rem(12)};

  &.rows-1 {
    grid-template-rows: repeat(1, min-content);
  }

  &.rows-2 {
    grid-template-rows: repeat(2, min-content);
  }

  &.rows-3 {
    grid-template-rows: repeat(3, min-content);
  }
`

interface Props {
  inclusions: Array<App.OfferInclusion>
  amenities: Array<App.OfferInclusion>
  description?: string
  onMoreClick: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void
}

function HotelCardFeatures({ inclusions, amenities, description, onMoreClick }: Props) {
  const intl = useIntl()
  const visibleAmenities = take(amenities, MAX_VISIBLE_AMENITIES)
  const hiddenFeatureCount = Math.max(0, inclusions.length - MAX_VISIBLE_INCLUSIONS)
  const handleMoreClick = useCallback((event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    trackClientEvent({ subject: 'read_more_inclusions', action: 'click', category: 'search_page', type: 'interaction' })
    onMoreClick(event)
  }, [onMoreClick])
  const moreFeatures = hiddenFeatureCount > 0 && <TextLink size="small" wrap="no-wrap" onClick={handleMoreClick}>
    {intl.formatMessage({
      id: 'AIwnWOOt3xcnn1pa1RAK',
      defaultMessage: '+{count} more',
      description: 'Link to show more truncated inclusions on search tile',
    }, { count: hiddenFeatureCount })}
  </TextLink>

  return <>
    {!!inclusions.length && <Group direction="vertical" gap={4}>
      {take(inclusions, MAX_VISIBLE_INCLUSIONS).map((inclusion, index) => <OfferInclusionItem
        key={inclusion.id}
        inclusion={inclusion}
        size="small"
        iconSize="XS"
        lineClamp={1}
        endIcon={index === MAX_VISIBLE_INCLUSIONS - 1 ? moreFeatures : undefined}
      />)}
    </Group>}
    {!!visibleAmenities.length && <AmenityGrid data-testid="chat-hotel-amenities" className={`rows-${Math.ceil(visibleAmenities.length / 2)}`}>
      {visibleAmenities.map(amenity => <SearchTileRowInclusionItem key={amenity.id} inclusion={amenity} variant="small" lineClamp={1} colour="light-foreground-alt" />)}
    </AmenityGrid>}
    {!!description && <BodyText variant="small" lineClamp={2}>{description}</BodyText>}
  </>
}

export default HotelCardFeatures
